import React, { useEffect, useState, useCallback } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { userDisplayName } from 'utils/userName';

interface AccountabilityOfferRow {
    _id: string;
    offerType: 'marketplace' | 'direct' | 'purchaseOrder';
    offerNumber?: string;
    offerBuyerNumber?: string;
    name?: string;
    industry?: string;
    subIndustry?: string;
    status?: string;
    sellerId?: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    buyerId?: { _id: string; name?: string; surname?: string; company?: string; email: string } | null;
    sellerPrice?: number;
    buyerPrice?: number;
    totalToDeliver?: number;
    leadApproved: number;
    leadPending: number;
    remainingToGenerate: number;
    notPaidCount: number;
    debtAmount: number;
    createdAt: string;
}

const formatEur = (n: number) =>
    n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const formatCount = (n: number) => n.toLocaleString('fr-FR');

const STATUS_LABELS: Record<string, string> = {
    on_market: 'On Market',
    running: 'Running',
    done: 'Done',
    waiting_subscription: 'Wait. Subscription',
    waiting_payment: 'Wait. Payment',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    pending_payment: 'Wait. Payment',
};

export default function BackOfficeAccountability() {
    const [rows, setRows] = useState<AccountabilityOfferRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [totalDebt, setTotalDebt] = useState(0);
    const [totalDebtOffers, setTotalDebtOffers] = useState(0);
    const [totalDebtPurchaseOrders, setTotalDebtPurchaseOrders] = useState(0);
    const [totalDebtApprovedNotPaid, setTotalDebtApprovedNotPaid] = useState(0);
    const [totalDebtPending, setTotalDebtPending] = useState(0);
    const [totalApproved, setTotalApproved] = useState(0);
    const [totalPending, setTotalPending] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page, limit: 50 };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            const res = await axios.get(`${CONFIG.apiUrl}/admin/accountability/offers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setRows(res.data.rows);
            setTotal(res.data.total);
            setTotalPages(res.data.pages);
            setTotalDebt(res.data.totalDebt);
            setTotalDebtOffers(res.data.totalDebtOffers);
            setTotalDebtPurchaseOrders(res.data.totalDebtPurchaseOrders);
            setTotalDebtApprovedNotPaid(res.data.totalDebtApprovedNotPaid);
            setTotalDebtPending(res.data.totalDebtPending);
            setTotalApproved(res.data.totalApproved);
            setTotalPending(res.data.totalPending);
        } catch (error) {
            console.error('Error fetching accountability:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'on_market':
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'running':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'done':
            case 'completed':
            case 'cancelled':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
            case 'waiting_subscription':
            case 'waiting_payment':
            case 'pending_payment':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            default:
                return 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-400';
        }
    };

    const getOfferPath = useCallback((row: AccountabilityOfferRow) => {
        if (row.offerType === 'purchaseOrder') return null;
        return `/back-office/offers/${row.offerType}/${row._id}`;
    }, []);

    const getPageNumbers = () => {
        const pages: (number | '...')[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (page > 3) pages.push('...');
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
            if (page < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const startItem = total === 0 ? 0 : (page - 1) * 50 + 1;
    const endItem = Math.min(page * 50, total);

    return (
        <div className="pt-[50px]">
            {/* Summary cards */}
            <div className="mb-5 grid grid-cols-2 gap-5 lg:grid-cols-5">
                <Card extra="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Purchase Orders</p>
                    <p className="mt-1 text-2xl font-bold text-red-500">{formatEur(totalDebtPurchaseOrders)}</p>
                    <p className="mt-1 text-xs text-gray-400">Reste à payer</p>
                </Card>
                <Card extra="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Offres</p>
                    <p className="mt-1 text-2xl font-bold text-red-500">{formatEur(totalDebtOffers)}</p>
                    <p className="mt-1 text-xs text-gray-400">Marketplace + Direct</p>
                </Card>
                <Card extra="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Leads approuvés</p>
                    <p className="mt-1 text-2xl font-bold text-orange-500">{formatEur(totalDebtApprovedNotPaid)}</p>
                    <p className="mt-1 text-xs text-gray-400">{formatCount(totalApproved)} leads · non payés</p>
                </Card>
                <Card extra="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Leads en attente</p>
                    <p className="mt-1 text-2xl font-bold text-yellow-500">{formatEur(totalDebtPending)}</p>
                    <p className="mt-1 text-xs text-gray-400">{formatCount(totalPending)} leads · à décider</p>
                </Card>
                <Card extra="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Total dette</p>
                    <p className="mt-1 text-2xl font-bold text-navy-700 dark:text-white">{formatEur(totalDebt)}</p>
                    <p className="mt-1 text-xs text-gray-400">Approuvés non payés + à générer</p>
                </Card>
            </div>

            <Card extra="p-5">
                {/* Header */}
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div>
                        <div className="text-2xl font-bold text-navy-700 dark:text-white">Comptabilité par offre</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {formatCount(total)} offres · dette triée par montant décroissant
                        </div>
                    </div>
                </div>

                {/* Search */}
                <div className="mb-5">
                    <div className="relative max-w-md">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Rechercher par offre, secteur, vendeur, acheteur..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:placeholder-gray-500"
                        />
                        {searchInput && (
                            <button
                                onClick={() => setSearchInput('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <MdClose size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Offre</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Secteur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Vendeur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Acheteur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Prix vendeur</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Leads</th>
                                <th className="pb-3 pr-4 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 pr-4 text-right text-xs font-bold uppercase tracking-wide text-red-600 dark:text-red-400">Dette</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-sm text-gray-500">Aucune offre trouvée</td>
                                </tr>
                            ) : (
                                rows.map((row) => {
                                    const path = getOfferPath(row);
                                    return (
                                        <tr
                                            key={`${row.offerType}-${row._id}`}
                                            className={`border-b border-gray-100 dark:border-navy-700 ${path ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700' : ''}`}
                                            onClick={() => path && navigate(path)}
                                        >
                                            {/* Offer */}
                                            <td className="py-3 pr-4">
                                                {row.offerNumber && (
                                                    <div className="font-mono text-xs text-navy-700 dark:text-white">
                                                        <span className="text-gray-400">S:</span> {row.offerNumber}
                                                    </div>
                                                )}
                                                {row.offerBuyerNumber && (
                                                    <div className="font-mono text-xs text-navy-700 dark:text-white">
                                                        <span className="text-gray-400">B:</span> {row.offerBuyerNumber}
                                                    </div>
                                                )}
                                                {!row.offerNumber && !row.offerBuyerNumber && (
                                                    <div className="font-mono text-xs text-gray-400">{row._id.slice(-8)}</div>
                                                )}
                                                {row.name && (
                                                    <div className="max-w-[120px] truncate text-xs text-gray-500 dark:text-gray-400" title={row.name}>
                                                        {row.name}
                                                    </div>
                                                )}
                                                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    row.offerType === 'marketplace'
                                                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                        : row.offerType === 'direct'
                                                        ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                                        : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                                }`}>
                                                    {row.offerType === 'marketplace' ? 'Marketplace' : row.offerType === 'direct' ? 'Direct' : 'Purchase Order'}
                                                </span>
                                            </td>

                                            {/* Industry */}
                                            <td className="py-3 pr-4 text-sm">
                                                <div className="font-medium text-navy-700 dark:text-white">{row.industry || '-'}</div>
                                                {row.subIndustry && <div className="text-xs text-gray-500">{row.subIndustry}</div>}
                                            </td>

                                            {/* Seller */}
                                            <td className="py-3 pr-4">
                                                {row.sellerId ? (
                                                    <div>
                                                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                                                            {userDisplayName(row.sellerId)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{row.sellerId.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                                                )}
                                            </td>

                                            {/* Buyer */}
                                            <td className="py-3 pr-4">
                                                {row.buyerId ? (
                                                    <div>
                                                        <div className="text-sm font-medium text-navy-700 dark:text-white">
                                                            {userDisplayName(row.buyerId)}
                                                        </div>
                                                        <div className="text-xs text-gray-500">{row.buyerId.email}</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                                                )}
                                            </td>

                                            {/* Seller price */}
                                            <td className="py-3 pr-4 text-sm text-gray-600 dark:text-gray-400">
                                                {row.sellerPrice != null ? formatEur(row.sellerPrice) : '-'}
                                            </td>

                                            {/* Leads */}
                                            <td className="py-3 pr-4">
                                                <div className="flex flex-col gap-0.5 text-xs">
                                                    <span className="font-semibold text-green-600 dark:text-green-400">
                                                        Approuvés: {row.leadApproved}
                                                        {row.totalToDeliver ? ` / ${row.totalToDeliver}` : ''}
                                                    </span>
                                                    <span className="text-yellow-600 dark:text-yellow-400">
                                                        En attente: {row.leadPending}
                                                    </span>
                                                    {row.remainingToGenerate > 0 && (
                                                        <span className="text-orange-500 dark:text-orange-400">
                                                            À générer: {row.remainingToGenerate}
                                                        </span>
                                                    )}
                                                    {row.notPaidCount > 0 && (
                                                        <span className="font-semibold text-red-500 dark:text-red-400">
                                                            Non payés: {row.notPaidCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="py-3 pr-4">
                                                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(row.status)}`}>
                                                    {STATUS_LABELS[row.status || ''] || row.status || 'N/A'}
                                                </span>
                                            </td>

                                            {/* Debt */}
                                            <td className="py-3 text-right">
                                                {row.debtAmount > 0 ? (
                                                    <span className="font-bold text-red-500 dark:text-red-400">
                                                        {formatEur(row.debtAmount)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="mt-5 flex items-center justify-between">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {total > 0 ? `${formatCount(startItem)}–${formatCount(endItem)} sur ${formatCount(total)} offres` : 'Aucune offre'}
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronLeft size={18} />
                                </button>
                                {getPageNumbers().map((p, i) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${i}`} className="px-2 text-sm text-gray-400">...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                                                page === p
                                                    ? 'bg-navy-700 text-white dark:bg-brand-400'
                                                    : 'border border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 dark:text-white'
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page === totalPages}
                                    className="flex items-center rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700"
                                >
                                    <MdChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
